/**
 * Marco del **portal del cliente** (raíz de su propio acceso, p. ej.
 * `https://portal.escienza.com.pe`).
 *
 * Sustituye a `views/portal_cliente/{header,footer}.php` del panel anterior:
 * cabecera con el logo, el nombre del cliente que entró y las secciones
 * (Resumen, Facturas, Consumos, Créditos, Pagos), más el botón de salir.
 *
 * Es deliberadamente sencillo y **sin** la navegación del panel: aquí no hay
 * módulos, permisos ni catálogo, sólo los datos del cliente que inició sesión.
 */

import { Car, Fuel, IdCard, LogOut, MailOpen, Menu, Receipt, Repeat, Send, Wallet, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { Logo, LogoMark } from '@/components/brand/logo';
import { Button } from '@/components/ui/button';
import { APP_META } from '@/config/app';
import { cn, initials } from '@/lib/utils';

import { usePortalMensajes, useSalirDelPortal } from '../portal.hooks';
import { selectClientePortal, usePortalStore } from '../portal.store';

interface Enlace {
  to: string;
  etiqueta: string;
  icon: LucideIcon;
}

const ENLACES: readonly Enlace[] = [
  { to: '/resumen', etiqueta: 'Resumen', icon: Fuel },
  { to: '/comprobantes', etiqueta: 'Comprobantes', icon: Receipt },
  { to: '/consumos', etiqueta: 'Consumos', icon: Repeat },
  { to: '/despachos', etiqueta: 'Despachos', icon: Send },
  { to: '/creditos', etiqueta: 'Créditos', icon: Wallet },
  { to: '/pagos', etiqueta: 'Pagos', icon: Wallet },
  { to: '/vehiculos', etiqueta: 'Vehículos', icon: Car },
  { to: '/conductores', etiqueta: 'Conductores', icon: IdCard },
  { to: '/mensajes', etiqueta: 'Mensajes', icon: MailOpen },
];

export function PortalShell() {
  const cliente = usePortalStore(selectClientePortal);
  const salir = useSalirDelPortal();
  const navegar = useNavigate();
  // El contador de mensajes sin leer se pide en el armazón para que se vea desde
  // cualquier pantalla del portal (y comparte caché con la pantalla de mensajes).
  const mensajes = usePortalMensajes();
  const sinLeer = mensajes.data?.sin_leer ?? 0;

  const [menuAbierto, setMenuAbierto] = useState(false);
  const cerrarRef = useRef<HTMLButtonElement>(null);

  // Menú lateral (móvil/tablet): bloquea el fondo mientras está abierto, enfoca
  // el botón de cerrar y se cierra con `Escape`.
  useEffect(() => {
    if (!menuAbierto) return;

    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    cerrarRef.current?.focus();

    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setMenuAbierto(false);
    };

    const alNavegar = () => setMenuAbierto(false);

    window.addEventListener('keydown', alTeclear);
    window.addEventListener('popstate', alNavegar);

    return () => {
      document.body.style.overflow = overflowPrevio;
      window.removeEventListener('keydown', alTeclear);
      window.removeEventListener('popstate', alNavegar);
    };
  }, [menuAbierto]);

  // Si la pantalla pasa a escritorio (o se gira una tablet), el menú lateral deja
  // de verse: se cierra para no dejar el fondo bloqueado con `overflow: hidden`.
  useEffect(() => {
    const escritorio = window.matchMedia('(min-width: 1024px)');
    const alCambiar = (evento: MediaQueryListEvent) => {
      if (evento.matches) setMenuAbierto(false);
    };

    escritorio.addEventListener('change', alCambiar);

    return () => escritorio.removeEventListener('change', alCambiar);
  }, []);

  const alSalir = () => {
    salir.mutate(undefined, {
      onSettled: () => navegar('/login', { replace: true }),
    });
  };

  return (
    <div className="portal-fondo relative flex min-h-dvh flex-col">
      {/*
       * Halos decorativos del fondo.
       *
       * Van dentro de un contenedor recortado (`overflow-hidden`) a propósito: al
       * estar desfasados hacia los lados se salían del contenedor y el navegador
       * contaba ese trozo como ancho de la página, lo que activaba el scroll
       * horizontal. No se puede resolver con `overflow-hidden` en la raíz porque
       * eso rompería la cabecera `sticky`.
       */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="portal-halo -top-32 -right-24" />
        <div
          className="portal-halo -left-40 bottom-10 opacity-20"
          style={{ background: 'radial-gradient(circle at 35% 35%, var(--color-accent-300), transparent 70%)' }}
        />
      </div>

      {/* Filete superior con el degradado de marca: da color a toda la app. */}
      <div aria-hidden="true" className="h-1 w-full bg-gradient-to-r from-brand-500 via-accent-400 to-brand-600" />

      <header className="sticky top-0 z-30 border-b border-white/70 bg-white/85 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={() => setMenuAbierto(true)}
            aria-label="Abrir menú"
            aria-expanded={menuAbierto}
            aria-controls="menu-portal"
            className="-ml-2 inline-flex size-10 shrink-0 items-center justify-center rounded-control text-ink-700 transition-colors hover:bg-ink-100 hover:text-ink-900 lg:hidden"
          >
            <Menu className="size-5" aria-hidden="true" />
          </button>

          <Logo size="sm" tagline="Portal del cliente" />

          <div className="ml-auto flex w-full min-w-0 items-center justify-end gap-2 sm:w-auto sm:gap-3">
            <div className="flex min-w-0 items-center gap-2.5 rounded-full bg-white py-1 pr-3 pl-1.5 ring-1 ring-ink-100 shadow-soft">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-accent-500 text-[11px] font-bold text-white">
                {initials(cliente?.nombre_comercial || cliente?.nombre)}
              </span>
              <span className="min-w-0">
                <span className="block max-w-[9rem] truncate text-sm font-semibold text-ink-900 sm:max-w-[14rem]">
                  {cliente?.nombre_comercial || cliente?.nombre || 'Cliente'}
                </span>
                <span className="block font-mono text-[11px] text-ink-400">{cliente?.numero_doc}</span>
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<LogOut className="size-3.5" aria-hidden="true" />}
              loading={salir.isPending}
              onClick={alSalir}
            >
              Salir
            </Button>
          </div>

          {/*
           * Escritorio: las nueve secciones en pastillas. En móvil y tablet se
           * ocultan y se abren desde el menú lateral (arrastrar la fila era
           * incómodo y escondía secciones sin que se notara).
           */}
          <nav className="-mb-1 hidden w-full items-center gap-1.5 overflow-x-auto pb-1 lg:flex lg:pb-0">
            {ENLACES.map((enlace) => (
              <NavLink
                key={enlace.to}
                to={enlace.to}
                end={enlace.to === '/resumen'}
                className={({ isActive }) =>
                  cn(
                    'inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-all',
                    isActive
                      ? 'bg-gradient-to-r from-brand-600 to-accent-500 text-white shadow-brand'
                      : 'bg-white text-ink-600 ring-1 ring-ink-100 hover:text-brand-700 hover:ring-brand-300',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <enlace.icon className="size-3.5" aria-hidden="true" />
                    {enlace.etiqueta}
                    {enlace.to === '/mensajes' && sinLeer > 0 ? (
                      <span
                        className={cn(
                          'ml-0.5 inline-flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold',
                          isActive ? 'bg-white/25 text-white' : 'bg-brand-500 text-white',
                        )}
                      >
                        {sinLeer}
                      </span>
                    ) : null}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      {/*
       * Menú lateral de móvil/tablet.
       *
       * Se mantiene montado (con `inert` cuando está cerrado) para poder animar
       * la entrada y la salida. Va por encima de la cabecera (`z-50`) y se cierra
       * al pulsar el fondo, la X, `Escape` o cualquier sección.
       */}
      <div
        className={cn('fixed inset-0 z-50 lg:hidden', menuAbierto ? 'pointer-events-auto' : 'pointer-events-none')}
        aria-hidden={!menuAbierto}
      >
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => setMenuAbierto(false)}
          className={cn(
            'absolute inset-0 h-full w-full cursor-default bg-ink-950/45 backdrop-blur-sm transition-opacity duration-200',
            menuAbierto ? 'opacity-100' : 'opacity-0',
          )}
        />

        <aside
          id="menu-portal"
          role="dialog"
          aria-modal="true"
          aria-label="Menú del portal"
          inert={!menuAbierto}
          className={cn(
            'absolute inset-y-0 left-0 flex w-[86%] max-w-xs flex-col bg-white shadow-2xl transition-transform duration-300 ease-out',
            menuAbierto ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex items-center justify-between gap-3 border-b border-ink-100 px-4 py-3">
            <Logo size="sm" tagline="Portal del cliente" />
            <button
              ref={cerrarRef}
              type="button"
              onClick={() => setMenuAbierto(false)}
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900"
              aria-label="Cerrar menú"
            >
              <X className="size-5" aria-hidden="true" />
            </button>
          </div>

          <div className="flex items-center gap-3 border-b border-ink-100 bg-gradient-to-r from-brand-50/80 to-white px-4 py-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-accent-500 text-xs font-bold text-white">
              {initials(cliente?.nombre_comercial || cliente?.nombre)}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-ink-900">
                {cliente?.nombre_comercial || cliente?.nombre || 'Cliente'}
              </span>
              <span className="block font-mono text-[11px] text-ink-400">
                {cliente?.numero_doc}
              </span>
            </span>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-3" aria-label="Secciones del portal">
            <ul className="flex flex-col gap-1">
              {ENLACES.map((enlace) => (
                <li key={enlace.to}>
                  <NavLink
                    to={enlace.to}
                    end={enlace.to === '/resumen'}
                    onClick={() => setMenuAbierto(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-control px-3 py-2.5 text-sm font-semibold transition-colors',
                        isActive
                          ? 'bg-gradient-to-r from-brand-600 to-accent-500 text-white shadow-brand'
                          : 'text-ink-700 hover:bg-brand-50 hover:text-brand-700',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <enlace.icon
                          className={cn('size-4 shrink-0', isActive ? 'text-white' : 'text-brand-500')}
                          aria-hidden="true"
                        />
                        <span className="flex-1">{enlace.etiqueta}</span>
                        {enlace.to === '/mensajes' && sinLeer > 0 ? (
                          <span
                            className={cn(
                              'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold',
                              isActive ? 'bg-white/25 text-white' : 'bg-brand-500 text-white',
                            )}
                          >
                            {sinLeer}
                          </span>
                        ) : null}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="border-t border-ink-100 p-3">
            <Button
              variant="outline"
              fullWidth
              leftIcon={<LogOut className="size-3.5" aria-hidden="true" />}
              loading={salir.isPending}
              onClick={alSalir}
            >
              Cerrar sesión
            </Button>
          </div>
        </aside>
      </div>

      <main className="relative mx-auto w-full max-w-6xl flex-1 px-4 py-7 sm:px-6">
        <Outlet />
      </main>

      <footer className="relative mt-6 bg-gradient-to-r from-ink-900 via-ink-900 to-ink-800">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-5 text-[11px] text-white/60 sm:px-6">
          <span className="flex items-center gap-2">
            <LogoMark className="size-5" />
            © {new Date().getFullYear()} {APP_META.vendor} · {APP_META.name}
          </span>
          <span className="rounded-full bg-white/10 px-2.5 py-1 font-mono text-white/80">
            v{APP_META.version}
          </span>
        </div>
      </footer>
    </div>
  );
}
